import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityIcon from "@mui/icons-material/Visibility";
// Removed material-ui-popup-state; using controlled MUI Menu
import SendIcon from "@mui/icons-material/Send";

import { toastMessage } from "utils/toastMessage";
import {
  approveProspect,
  rejectProspect,
  sendForApproval,
} from "_controllers/officer";
import { useSelector } from "react-redux";
import DealerProspectView from "./DealerProspectView";

import authAxios from "setups/axios";
import { BusinessCenter, DataObject } from "@mui/icons-material";

// Status chip for an onboarding stage. When a stage `message` is available
// (e.g. "Sales approval pending from x@mail.com since ...") it is revealed on
// hover for every status, not just failures.
const StatusChip = ({ status, message, successLabel }) => {
  const cleanStatus = (status ?? "").toString().trim().toLowerCase();

  let chip;
  if (!cleanStatus || cleanStatus === "n/a" || cleanStatus === "false") {
    chip = (
      <Chip
        size="small"
        label="N/A"
        variant="outlined"
        sx={{ color: "text.secondary", borderColor: "grey.300", bgcolor: "grey.50", fontWeight: 500 }}
      />
    );
  } else if (["accepted", "approved", "success", "paid", "true"].includes(cleanStatus)) {
    const hasSuccessLabel =
      successLabel && successLabel.toString().trim() && successLabel !== "N/A";
    chip = (
      <Chip
        size="small"
        label={hasSuccessLabel ? successLabel : status.toString().toUpperCase()}
        color="success"
        sx={{ fontWeight: "bold" }}
      />
    );
  } else if (["pending", "initiated", "in progress", "waiting"].includes(cleanStatus)) {
    chip = (
      <Chip
        size="small"
        label={status.toString().toUpperCase()}
        color="warning"
        sx={{ fontWeight: "bold" }}
      />
    );
  } else if (["rejected", "failed", "error"].includes(cleanStatus)) {
    chip = (
      <Chip
        size="small"
        label={status.toString().toUpperCase()}
        color="error"
        sx={{ fontWeight: "bold" }}
      />
    );
  } else {
    chip = (
      <Chip
        size="small"
        label={status}
        variant="outlined"
        sx={{ color: "primary.main", borderColor: "primary.light", fontWeight: "bold" }}
      />
    );
  }

  const hoverMessage =
    message && message.toString().trim() && message !== "N/A" ? message : "";
  return hoverMessage ? (
    <Tooltip title={hoverMessage} arrow>
      <span style={{ cursor: "help" }}>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
};

// Vertical timeline of the onboarding stages shown in the Onboard dialog:
// "Sales Approval Accepted", "Payment Pending", ... with the stored stage
// message (who it is pending with / when it was decided) underneath.
const STAGE_TIMELINE_DEFS = [
  { key: "soApproval", msgKey: "soApprovalMessage", label: "Sales Admin Approval" },
  {
    key: "finalApproval",
    msgKey: "finalApprovalMessage",
    label: "Final Approval",
  },
  { key: "paymentStatus", msgKey: "paymentStatusMessage", label: "Payment" },
  { key: "sapStatus", msgKey: "sapStatusMessage", label: "SAP Dealer Creation" },
];

const stageStatusWord = (status) => {
  const s = (status || "").toString().trim().toLowerCase();
  if (["accepted", "approved", "true"].includes(s)) return "Accepted";
  if (s === "success") return "Successful";
  if (s === "paid") return "Paid";
  if (["pending", "initiated", "waiting", "in progress"].includes(s))
    return "Pending";
  if (s === "rejected") return "Rejected";
  if (["failed", "error"].includes(s)) return "Failed";
  return "Not Started";
};

const stageStatusColor = (word) => {
  if (["Accepted", "Successful", "Paid"].includes(word)) return "success.main";
  if (word === "Pending") return "warning.main";
  if (["Rejected", "Failed"].includes(word)) return "error.main";
  return "text.disabled";
};

const StageTimeline = ({ ub }) => (
  <Box sx={{ mb: 2, pt: 0.5 }}>
    {STAGE_TIMELINE_DEFS.map((def, idx) => {
      const word = stageStatusWord(ub?.[def.key]);
      const color = stageStatusColor(word);
      const message =
        def.key === "sapStatus"
          ? ub?.sapStatusMessage || ub?.onboardingFailsReason
          : ub?.[def.msgKey];
      const isLast = idx === STAGE_TIMELINE_DEFS.length - 1;
      return (
        <Box key={def.key} sx={{ display: "flex", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: color,
                mt: "5px",
                flexShrink: 0,
              }}
            />
            {!isLast && (
              <Box
                sx={{
                  width: 2,
                  flex: 1,
                  bgcolor: "grey.300",
                  my: "2px",
                  minHeight: 18,
                }}
              />
            )}
          </Box>
          <Box sx={{ pb: isLast ? 0 : 1.5, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {def.label}{" "}
              <Box component="span" sx={{ color }}>
                {word}
              </Box>
            </Typography>
            {message && message !== "N/A" ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", wordBreak: "break-word" }}
              >
                {message}
              </Typography>
            ) : null}
          </Box>
        </Box>
      );
    })}
  </Box>
);

// Decide what the Onboard button should do for a row, mirroring the backend
// saveUserOnBoard stage chain. Returns null when onboarding has not started
// (open the dealer form directly); otherwise { label, description, onboarded }
// for the stage dialog — label is the API action run with the saved details.
const getStageAction = (ub) => {
  const lc = (v) => (v || "").toString().trim().toLowerCase();
  const ok = (v) => ["accepted", "success", "paid"].includes(lc(v));
  const bad = (v) => ["rejected", "failed", "error"].includes(lc(v));
  const waiting = (v) =>
    ["pending", "initiated", "in progress", "waiting"].includes(lc(v));
  const started = (v) => ok(v) || bad(v) || waiting(v);

  if (!ub || !started(ub.soApproval)) return null;

  if (ub.userOnBoard === true || ub.userOnBoard === "true" || ok(ub.sapStatus)) {
    return {
      onboarded: true,
      label: null,
      description: `Dealer is fully onboarded${
        ub.kunnr && ub.kunnr !== "N/A" ? ` — SAP customer ID ${ub.kunnr}` : ""
      }.`,
    };
  }
  if (ok(ub.paymentStatus)) {
    return {
      label: bad(ub.sapStatus)
        ? "Retry SAP Dealer Creation"
        : "Create Dealer in SAP",
      description: bad(ub.sapStatus)
        ? ub.sapStatusMessage ||
          ub.onboardingFailsReason ||
          "SAP dealer creation failed. Retry with the saved details, or edit them first."
        : ub.sapStatusMessage ||
          "Payment received. Proceed to create the dealer in SAP with the saved details.",
    };
  }
  if (ok(ub.soApproval) && ok(ub.finalApproval)) {
    return {
      label:
        waiting(ub.paymentStatus) || bad(ub.paymentStatus)
          ? "Regenerate Payment Link"
          : "Generate Payment Link",
      description:
        ub.paymentStatusMessage ||
        "Approvals complete. Proceed to generate the dealer payment link.",
    };
  }
  if (ok(ub.soApproval)) {
    return {
      label: started(ub.finalApproval)
        ? "Send Final Approval Again"
        : "Send for Final Approval",
      description:
        ub.finalApprovalMessage ||
        "Sales admin approval done. Proceed to send the final approval email.",
    };
  }
  return {
    label: "Send Sales Admin Approval Again",
    description:
      ub.soApprovalMessage ||
      (bad(ub.soApproval)
        ? "Sales admin approval was rejected."
        : "Sales admin approval is pending."),
  };
};

const ProspectTable = ({
  data,
  header,
  setReload,
  remarkAction,
  openPreview,
  setOpenPreview,
  setProspectInfo,
  prospectInfo,
  openTimeline,
  setOpenTimeline,
  setProspectId1,
}) => {
  let user = useSelector((state) => state.user);

  const history = useHistory();

  const [userData, setUserData] = useState();
  const [open, setOpen] = useState(false);

  const sendToActyv = async (rowData) => {
    let data = await sendForApproval({ id: rowData._id, type: rowData.type });
    if (data?.status === "failure") {
      toastMessage(data?.message, data?.status);
    } else if (data?.status === "success") {
      setReload(rowData?._id);
      toastMessage("Prospect sent to Actyv", "success");
    }
  };

  console.log("prospect", data);
  const rejectAction = async (prospect) => {
    await rejectProspect(prospect.prospect_id, prospect.prospectType)
      .then((d) => {
        console.log("data", d);
        setReload(prospect.prospect_id);
        toastMessage(d?.data?.message, "success");
      })
      .catch((err) => {
        console.log("err", err);
        // setLoading(false);
        toastMessage("error fetching data", "error");
      });
  };
  const approveAction = async (prospect) => {
    await approveProspect(prospect.prospect_id, prospect.prospectType)
      .then((d) => {
        console.log("data", d);
        setReload(prospect.prospect_id);
        toastMessage(d?.data?.message, "success");
      })
      .catch((err) => {
        console.log("err", err);
        // setLoading(false);
        toastMessage("error fetching data", "error");
      });
  };
  const sendForE_Contract = async (rowData) => {
    // Perform the action for Button 2

    console.log("Button 2 clicked for row data:", rowData);
    const url = process.env.REACT_APP_REQUEST_ENDPOINT;

    const response = await authAxios()({
      url: `/api/v1/actyv/prospect/payment/${rowData?._id}?type=PROSPECT`,
      method: "POST",
    });
    console.log("res", response);
  };

  const reviewStatus = (rowData) => {
    // Perform the action for Button 3
    console.log("Button 3 clicked for row data:", rowData);
    toastMessage("Feature Under Development", "success");
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Menu state for actions per row
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuProspect, setMenuProspect] = useState(null);
  // Stage dialog for the Onboard button: { row, action } | null
  const [stageDialog, setStageDialog] = useState(null);
  const [stageActionLoading, setStageActionLoading] = useState(false);

  const openDealerForm = (row) => {
    const dealerId = row?._id;
    history.push(`/actyv-ai/dealer-form?dealerId=${dealerId}`, {
      from: "prospect_table",
      prospectId: dealerId,
      dealerId,
    });
  };

  // Re-run the current onboarding stage with the details already saved on the
  // backend (no form edit): fetch the saved record and re-submit it as-is.
  const proceedWithSavedDetails = async () => {
    const row = stageDialog?.row;
    if (!row?._id) return;
    setStageActionLoading(true);
    try {
      const saved = await authAxios()({
        url: `/api/v1/actyv/onboarding/user?prospectId=${row._id}`,
        method: "GET",
      });
      const form = saved?.data?.data;
      if (!form || typeof form !== "object") {
        toastMessage(
          "No saved onboarding details found. Please use Edit Details first.",
          "error"
        );
        return;
      }
      const res = await authAxios()({
        url: "/api/v1/actyv/onboarding/user",
        method: "POST",
        data: { prospectId: row._id, form },
      });
      toastMessage(
        res?.data?.message || "Onboarding step triggered successfully",
        "success"
      );
      setStageDialog(null);
      setReload(`${row._id}-${Date.now()}`);
    } catch (err) {
      toastMessage(
        err?.response?.data?.message || "Failed to trigger the onboarding step",
        "error"
      );
    } finally {
      setStageActionLoading(false);
    }
  };
  const isMenuOpen = Boolean(menuAnchorEl);
  const openMenu = (event, prospect) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuProspect(prospect);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuProspect(null);
  };

  console.log("data", data,"header", header);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        <Table stickyHeader>
          {header && header.length > 0 ? (
            <TableHead>
              <TableRow>
                {header.map((item, idx) => (
                  <TableCell
                    align="left"
                    sx={{ fontWeight: "bolder" }}
                    key={idx}
                  >
                    {item.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          ) : null}

          <TableBody>
            {data && data?.length > 0 ? (
              data?.map((d) => (
                <TableRow key={d?._id}>
                  <TableCell align="left">
                    {d?.createdBy?.SAP_ID ?? "-"}
                  </TableCell>
                  <TableCell align="left">
                    {d?.businessInfo?.dealershipName ?? "-"}
                  </TableCell>
                  <TableCell align="left">
                    {d?.businessInfo?.email ?? "-"}
                  </TableCell>
                  <TableCell align="left">
                    {d?.businessInfo?.phoneNumber ?? "-"}
                  </TableCell>
                  {/* <TableCell align="left">
                    {d?.basicInfo?.firstPersonContact
                      ? d?.basicInfo?.firstPersonContact?.name
                      : "-"}
                  </TableCell> */}
                  {/* <TableCell align="left">
                    {d?.basicInfo?.firstPersonContact
                      ? moment
                        .utc(d?.basicInfo?.firstPersonContact?.dob)
                        .format("YYYY-MM-DD")
                      : "-"}
                  </TableCell> */}
                  <TableCell align="left">
                    {d?.basicInfo?.firstPersonContact
                      ? d?.basicInfo?.firstPersonContact?.phoneNumber
                      : "-"}
                  </TableCell>
                  <TableCell align="left">
                    <VisibilityIcon
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setOpen(true);
                        setUserData(d);
                      }}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Button
                      variant="contained"
                      onClick={(e) => openMenu(e, d?.prosStatus)}
                      disabled={
                        !d?.prosStatus
                          ? true
                          : d?.prosStatus?.status?.includes("Pending")
                          ? false
                          : user?.user?.role == "ADMIN" &&
                            d?.prosStatus?.status == "Pending_HO"
                          ? false
                          : true
                      }
                    >
                      {d?.prosStatus ? d?.prosStatus?.status : "N/A"}
                    </Button>
                    <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={closeMenu}>
                      <MenuItem
                        onClick={() => {
                          if (menuProspect) approveAction(menuProspect);
                          closeMenu();
                        }}
                      >
                        Approve
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (menuProspect) rejectAction(menuProspect);
                          closeMenu();
                        }}
                      >
                        Reject
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (menuProspect) remarkAction({ close: closeMenu }, menuProspect);
                          closeMenu();
                        }}
                      >
                        Remark
                      </MenuItem>
                    </Menu>
                  </TableCell>
                  {/* {user.user.role == "ADMIN" ? */}
                  <TableCell align="left">
                    <Button
                      onClick={() => sendToActyv(d)}
                      variant="contained"
                      endIcon={<SendIcon />}
                      disabled={
                        d?.detailSendToActyv
                          ? true
                          : d?.prosStatus?.status === "Rejected"
                          ? true
                          : false
                      }
                      style={{ fontSize: "12px" }}
                    >
                      approval
                    </Button>
                  </TableCell>
                  {/* : ""
                  } */}
                  <TableCell align="left">
                    <Button
                      variant="contained"
                      disabled={d?.documentLinks?.length > 0 ? false : true}
                      onClick={() => {
                        setOpenPreview(true);
                        setProspectInfo(d);
                      }}
                    >
                      Preview
                    </Button>
                  </TableCell>
                  <TableCell align="left">
                    <Button
                      onClick={() => sendForE_Contract(d)}
                      variant="contained"
                      endIcon={<SendIcon />}
                      style={{ fontSize: "12px" }}
                    >
                      send
                    </Button>
                  </TableCell>
                  <TableCell align="left">
                    <IconButton
                      size="large"
                      color="primary"
                      onClick={() => {
                        setOpenTimeline(true);
                        setProspectId1(d?._id);
                      }}
                    >
                      <RemoveRedEyeIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="left">
                    <Button
                      variant="contained"
                      onClick={() => {
                        const action = getStageAction(d?.userOnBoard);
                        // No onboarding started yet → open the form directly.
                        if (!action) {
                          openDealerForm(d);
                          return;
                        }
                        setStageDialog({ row: d, action });
                      }}
                      style={{ fontSize: "12px" }}
                    >
                      Onboard
                    </Button>
                  </TableCell>

                  <TableCell align="left">
                    <StatusChip
                      status={d?.userOnBoard?.soApproval}
                      message={d?.userOnBoard?.soApprovalMessage}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <StatusChip
                      status={d?.userOnBoard?.finalApproval}
                      message={d?.userOnBoard?.finalApprovalMessage}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <StatusChip
                      status={d?.userOnBoard?.paymentStatus}
                      message={d?.userOnBoard?.paymentStatusMessage}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <StatusChip
                      status={d?.userOnBoard?.sapStatus}
                      message={
                        d?.userOnBoard?.sapStatusMessage ||
                        d?.userOnBoard?.onboardingFailsReason
                      }
                      successLabel={d?.userOnBoard?.kunnr}
                    />
                  </TableCell>
                  {/* <TableCell align="left">
                    <IconButton
                      size="large"
                      color="primary"
                      onClick={() => {
                        setOpenTimeline(true);
                        setProspectId1(d?._id);
                      }}
                    >
                      <BusinessCenter fontSize="inherit" />
                    </IconButton>
                  </TableCell> */}

                  {/* <TableCell align="left">
                    <Button onClick={() => reviewStatus(d)} variant="contained">
                      status
                    </Button>
                  </TableCell> */}
                </TableRow>
              ))
            ) : (
              <Box sx={{ textAlign: "left", pt: 1 }}>
                <Typography variant="h6" sx={{ p: 1 }}>
                  No Data.
                </Typography>
              </Box>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {open ? (
        <DealerProspectView
          handleClose={handleClose}
          data={userData}
          remarkAction={remarkAction}
          setReload={setReload}
        />
      ) : null}

      {/* Stage dialog for the Onboard button: proceed with the saved details
          (runs the next backend step) or edit the details in the form. */}
      <Dialog
        open={Boolean(stageDialog)}
        onClose={() => {
          if (!stageActionLoading) setStageDialog(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Dealer Onboarding
          {stageDialog?.row?.businessInfo?.dealershipName
            ? ` — ${stageDialog.row.businessInfo.dealershipName}`
            : ""}
        </DialogTitle>
        <DialogContent>
          <StageTimeline ub={stageDialog?.row?.userOnBoard} />
          <Typography variant="body2">
            {stageDialog?.action?.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStageDialog(null)}
            disabled={stageActionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={stageActionLoading}
            onClick={() => {
              const row = stageDialog?.row;
              setStageDialog(null);
              openDealerForm(row);
            }}
          >
            {stageDialog?.action?.onboarded ? "View Details" : "Edit Details"}
          </Button>
          {stageDialog?.action?.label ? (
            <Button
              variant="contained"
              disabled={stageActionLoading}
              startIcon={
                stageActionLoading ? <CircularProgress size={16} /> : null
              }
              onClick={proceedWithSavedDetails}
            >
              {stageDialog.action.label}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProspectTable;
